import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../api/api';
import { User } from '../../types';

interface UserState {
  users: User[];
  currentUser: User | null;
  loading: boolean;
  error: string | null;
}

export const fetchUsers = createAsyncThunk('user/fetchAll', async () => {
  const res = await api.get<User[]>('/users');
  return res.data;
});

export const fetchCurrentUser = createAsyncThunk('user/fetchMe', async () => {
  const res = await api.get<User>('/users/me');
  return res.data;
});

export const fetchUserById = createAsyncThunk('user/fetchById', async (id: string) => {
  const res = await api.get<User>(`/users/${id}`);
  return res.data;
});

export const updateProfile = createAsyncThunk(
  'user/updateProfile',
  async ({ userId, data }: { userId: string; data: any }) => {
    const res = await api.put<User>(`/users/${userId}/profile`, data);
    return res.data;
  }
);

export const assignRole = createAsyncThunk(
  'user/assignRole',
  async ({ userId, roleId }: { userId: number; roleId: number }) => {
    await api.post(`/users/${userId}/roles`, roleId);
    return { userId, roleId };
  }
);

const initialState: UserState = {
  users: [],
  currentUser: null,
  loading: false,
  error: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.users = action.payload;
        state.loading = false;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load users';
      })
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.currentUser = action.payload;
        state.loading = false;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load current user';
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.currentUser = action.payload;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.currentUser = action.payload;
        state.users = state.users.map((user) => (user.id === action.payload.id ? action.payload : user));
      });
  },
});

export default userSlice.reducer;
